import { showToast } from "./toasts";

export interface CountdownState {
  secondsUntilStart: number | null;
  isActive: boolean;
  text?: string;
}

export interface CountdownElements {
  overlayEl: HTMLElement | null;
  numberEl: HTMLElement | null;
  textEl: HTMLElement | null;
}

const elements: CountdownElements = {
  overlayEl: null,
  numberEl: null,
  textEl: null,
};

const TICK_INTERVAL_MS = 100;

let ticker: ReturnType<typeof setInterval> | null = null;
let isCountdownActive = false;
let serverAnchorSeconds = 0;
let serverAnchorAtMs = 0;
let lastServerSeconds: number | null = null;
let displayedSeconds: number | null = null;
let countdownText = "Game Starting";
let lastToastSecond: number | null = null;

/**
 * Initialize countdown timer UI component
 * Caches DOM element references for performance
 */
export function initializeCountdown(): void {
  elements.overlayEl = document.getElementById("countdownOverlay");
  elements.numberEl = document.getElementById("countdownNumber");
  elements.textEl = document.getElementById("countdownText");
}

/**
 * Update countdown display with animation
 * Handles show/hide and number updates with pulse animation
 */
export function updateCountdown(state: CountdownState): void {
  if (state.isActive && state.secondsUntilStart !== null) {
    syncCountdownFromServer(
      state.secondsUntilStart,
      state.text || "Game Starting",
    );
  } else {
    hideCountdown();
  }
}

function syncCountdownFromServer(seconds: number, text: string): void {
  countdownText = text;

  if (!isCountdownActive || lastServerSeconds !== seconds) {
    serverAnchorSeconds = Math.max(0, seconds);
    serverAnchorAtMs = performance.now();
    lastServerSeconds = seconds;
    isCountdownActive = true;
    ensureTicker();
    renderInterpolatedCountdown();
    return;
  }

  // Server value unchanged: keep smooth local interpolation, just update text.
  if (elements.overlayEl) {
    elements.overlayEl.classList.add("show");
  }
  if (elements.textEl) {
    elements.textEl.textContent = countdownText;
  }
}

function ensureTicker(): void {
  if (ticker !== null) {
    return;
  }
  ticker = setInterval(renderInterpolatedCountdown, TICK_INTERVAL_MS);
}

function clearTicker(): void {
  if (ticker === null) {
    return;
  }
  clearInterval(ticker);
  ticker = null;
}

function renderInterpolatedCountdown(): void {
  if (!isCountdownActive) {
    return;
  }

  const elapsedSeconds = (performance.now() - serverAnchorAtMs) / 1000;
  const nextSeconds = Math.max(
    0,
    Math.ceil(serverAnchorSeconds - elapsedSeconds),
  );

  if (elements.overlayEl) {
    elements.overlayEl.classList.add("show");
  }

  if (elements.numberEl && displayedSeconds !== nextSeconds) {
    elements.numberEl.textContent = nextSeconds.toString();
    elements.numberEl.style.animation = "none";
    void elements.numberEl.offsetHeight;
    elements.numberEl.style.animation = "countdownPulse 1s ease-in-out";
    displayedSeconds = nextSeconds;
    showCountdownToasts(nextSeconds);
  }

  if (elements.textEl) {
    elements.textEl.textContent = countdownText;
  }
}

function hideCountdown(): void {
  isCountdownActive = false;
  serverAnchorSeconds = 0;
  serverAnchorAtMs = 0;
  lastServerSeconds = null;
  displayedSeconds = null;
  lastToastSecond = null;
  clearTicker();

  if (elements.overlayEl) {
    elements.overlayEl.classList.remove("show");
  }

  if (elements.numberEl) {
    elements.numberEl.textContent = "";
  }
  if (elements.textEl) {
    elements.textEl.textContent = "";
  }
}

function showCountdownToasts(seconds: number): void {
  if (lastToastSecond === seconds) {
    return;
  }
  lastToastSecond = seconds;

  if (seconds === 10) {
    showToast("Game starting in 10 seconds...", "info", 2000);
  } else if (seconds === 5) {
    showToast("Game starting in 5 seconds...", "warning", 2000);
  } else if (seconds === 3) {
    showToast("Get ready!", "warning", 1000);
  }
}

/**
 * Get current countdown elements (for debugging)
 */
export function getCountdownElements(): CountdownElements {
  return elements;
}

/**
 * Force hide countdown (utility function)
 */
export function forceHideCountdown(): void {
  hideCountdown();
}
